import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Source, Layer } from 'react-map-gl';
import { formatInTimeZone } from 'date-fns-tz';
import debounce from 'lodash/debounce';
import './FOVLayer.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// Utility function for formatting dates in UTC
const formatDateUTC = (date) => {
    return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
};

const FieldOfViewLayer = ({ layerId, visible, currentDateTime, isPlaying }) => {
    const [geoJsonFeatures, setGeoJsonFeatures] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const requestCancelRef = useRef(false);
    const prevDateRef = useRef(null);
    const hasDataRef = useRef(false);
    
    // Use a simplified flight level state with FL320 active
    // This requests filtered data (~9MB) instead of all levels (23MB)
    const [flightLevelVisibility, setFlightLevelVisibility] = useState({
        FL320: true
    });

    // Get active flight levels as a comma-separated string for API calls
    const activeFlightLevels = useMemo(() => {
        const levels = Object.entries(flightLevelVisibility)
            .filter(([_, isVisible]) => isVisible)
            .map(([level]) => level)
            .join(',');
        // console.log('Active flight levels:', levels);
        return levels;
    }, [flightLevelVisibility]);

    // Fetch GeoJSON data from the backend (pre-processed)
    const fetchGeoJSON = useCallback(async (dateStr, flightLevels) => {
        if (!visible) return;

        // Skip if date hasn't changed and we already have data
        if (prevDateRef.current === dateStr && hasDataRef.current) {
            return;
        }

        setIsLoading(true);
        requestCancelRef.current = false;
        prevDateRef.current = dateStr;

        try {
            let url = `${API_BASE_URL}/fov_geojson?date_str=${dateStr}`;
            if (flightLevels) {
                url += `&flight_levels=${encodeURIComponent(flightLevels)}`;
            }

            const response = await fetch(url);

            // If another request was made, cancel this one
            if (requestCancelRef.current) return;

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.features && data.features.length > 0) {
                hasDataRef.current = true;
                setGeoJsonFeatures(data.features);

                // Update available flight levels from received data
                const newFlightLevels = {};
                data.features.forEach(feature => {
                    const flightLevel = feature.properties?.flightLevel;
                    if (flightLevel) {
                        newFlightLevels[flightLevel] = flightLevel === 'FL320';
                    }
                });

                if (Object.keys(newFlightLevels).length > 0) {
                    setFlightLevelVisibility(prev => ({...prev, ...newFlightLevels}));
                }
            } else {
                hasDataRef.current = false;
                setGeoJsonFeatures([]);
            }
        } catch (error) {
            console.error('Error fetching GeoJSON data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [visible]);

    // Debounce the fetch for non-playing mode
    const debouncedFetchGeoJSON = useMemo(
        () => debounce(fetchGeoJSON, 300),
        [fetchGeoJSON]
    );
    
    // Effect to fetch data when relevant props change
    useEffect(() => {
        if (!visible) return;

        const currentDateStr = formatDateUTC(currentDateTime);

        // Cancel any pending requests
        requestCancelRef.current = true;

        if (isPlaying) {
            fetchGeoJSON(currentDateStr, activeFlightLevels);
        } else {
            debouncedFetchGeoJSON(currentDateStr, activeFlightLevels);
        }

        return () => {
            requestCancelRef.current = true;
            debouncedFetchGeoJSON.cancel();
        };
    }, [
        visible,
        currentDateTime,
        isPlaying,
        fetchGeoJSON,
        debouncedFetchGeoJSON,
        activeFlightLevels
    ]);
    
    // Memoize the flight levels for rendering in the UI
    const flightLevelsList = useMemo(() => 
        Object.keys(flightLevelVisibility).sort(), 
        [flightLevelVisibility]
    );
    
    // Create GeoJSON data with all features
    const geoJsonData = useMemo(() => {
        // Filter based on flight level visibility
        const filteredFeatures = geoJsonFeatures.filter(
            feature => feature.properties?.flightLevel && 
            flightLevelVisibility[feature.properties.flightLevel]
        );
        
        return {
            type: 'FeatureCollection',
            features: filteredFeatures,
        };
    }, [geoJsonFeatures, flightLevelVisibility]);
    
    // Flight level toggle handler
    const toggleFlightLevelVisibility = useCallback((flightLevel) => {
        setFlightLevelVisibility(prev => ({
            ...prev, 
            [flightLevel]: !prev[flightLevel]
        }));
    }, []);
    
    if (!visible) {
        return null;
    }
    
    return (
        <>
            <div className="flight-level-selector">
                {flightLevelsList.map((flightLevel) => (
                    <label key={flightLevel} style={{ marginRight: '10px' }}>
                        <input
                            type="checkbox"
                            checked={flightLevelVisibility[flightLevel]}
                            onChange={() => toggleFlightLevelVisibility(flightLevel)}
                        />
                        {flightLevel}
                    </label>
                ))}
                {isLoading && <span className="loading-indicator">Loading...</span>}
            </div>

            <Source
                id={`${layerId}-fov`}
                type="geojson"
                data={geoJsonData}
            >
                <Layer
                    id={`${layerId}-fov-fill`}
                    type="fill"
                    paint={{
                        'fill-color': 'rgba(0, 255, 255, 0.5)',
                        'fill-outline-color': 'rgba(0, 255, 255, 1)',
                        'fill-opacity': 0.3,
                    }}
                />
                <Layer
                    id={`${layerId}-fov-outline`}
                    type="line"
                    paint={{
                        'line-color': 'rgba(0, 255, 255, 1)',
                        'line-width': 1,
                    }}
                />
            </Source>
        </>
    );
};

export default React.memo(FieldOfViewLayer);